import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, FileText } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { cn } from "@/lib/cn";
import { Input } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";
import { Card } from "@/components/ui-kit/Card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { StatusBadge } from "@/components/ui-kit/StatusBadge";
import { useFunder } from "@/funder/FunderContext";

const STATUSES = ["All", "Draft", "Sent", "PartiallyPaid", "Paid", "Superseded", "Void"];

export function InvoicesListPage() {
  const navigate = useNavigate();
  const { abbr, payerLabel } = useFunder();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [payer, setPayer] = useState("All");
  const [status, setStatus] = useState("All");

  useEffect(() => {
    api.get("/invoices").then((r) => setRows(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (payer !== "All" && r.payer !== payer) return false;
      if (status !== "All" && r.status !== status) return false;
      if (!q) return true;
      return (
        (r.number || "").toLowerCase().includes(q) ||
        (r.client_name || "").toLowerCase().includes(q) ||
        (r.matter_name || "").toLowerCase().includes(q)
      );
    });
  }, [rows, query, payer, status]);

  return (
    <div className="space-y-6" data-testid="invoices-list-page">
      <div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">Invoices</h1>
        <p className="mt-2 text-sm text-muted-foreground">Every issued invoice across all matters — frozen snapshots.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="invoice-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search number / client / matter…" className="pl-9" />
        </div>
        <Select value={payer} onChange={(e) => setPayer(e.target.value)} data-testid="invoice-payer-filter" className="max-w-[160px]">
          <option value="All">All payers</option>
          <option value="Client">Client</option>
          <option value="ACSA">{abbr}</option>
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="invoice-status-filter" className="max-w-[180px]">
          {STATUSES.map((s) => <option key={s} value={s}>{s === "All" ? "All statuses" : s}</option>)}
        </Select>
      </div>

      <Card>
        {error ? (
          <div className="p-6 text-sm text-destructive">{error}</div>
        ) : rows === null ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-lg bg-secondary text-muted-foreground"><FileText className="h-6 w-6" /></span>
            <p className="text-sm text-muted-foreground">No invoices match.</p>
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Number</TH><TH>Client / Matter</TH><TH>Payer</TH><TH>Issue date</TH>
                <TH className="text-right">Amount</TH><TH>Status</TH><TH className="text-right">Amount due</TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((i) => (
                <TR key={i.id} data-testid={`invoice-row-${i.id}`} className="cursor-pointer" onClick={() => navigate(`/invoices/${i.id}`)}>
                  <TD className="font-medium text-foreground">{i.number || "Draft"}</TD>
                  <TD>
                    <span className="text-foreground">{i.client_name || "—"}</span>
                    <span className="text-muted-foreground"> · {i.matter_name || "—"}</span>
                  </TD>
                  <TD className="text-muted-foreground">{payerLabel(i.payer)}</TD>
                  <TD className="text-muted-foreground">{i.issue_date ? shortDate(i.issue_date) : "—"}</TD>
                  <TD className="nums text-right">{i.amount != null ? money(i.amount) : "—"}</TD>
                  <TD><StatusBadge status={i.status} /></TD>
                  <TD className={cn("nums text-right", i.amount_due_now > 0 ? "font-medium text-foreground" : "text-muted-foreground")}>{money(i.amount_due_now)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
